import { t } from '@sm/intl';
import { isValidEmail } from '@sm/utils';

import COPY from '~pricing/pages/Enterprise/copy';
import { ValidationResult, ValidatorFn, ValidationColor, FIELD_TYPE, FormState } from './types';

const FIELDS = Object.freeze({ email: 'email', phone: 'phone', defaultType: 'defaultType' });

export const ERROR_MAP = {
  INVALID_EMAIL: t(COPY.INVALID_EMAIL),
  BLOCKED_EMAIL: t(COPY.BLOCKED_EMAIL),
  INVALID_PHONE_NUMBER: t(COPY.INVALID_PHONE_NUMBER),
  REQUIRED_FIELD: t(COPY.REQUIRED),
  GENERIC_ERROR: t(COPY.ERROR),
};

const PHONE_NUMBER_REGEX = new RegExp(
  '^(\\+\\d{1,2}\\s?)?1?\\-?\\.?\\s?\\(?\\d{3}\\)?[\\s.-]?\\d{3}[\\s.-]?\\d{4}$',
  's'
);

const VALIDATOR_SUCCESS: ValidationResult = { color: ValidationColor.success };

/**
 * This applies to most specific validator to an input
 * @param param ValidatorFn
 * @returns a validation result in accordance with Wrench input component
 */
export function applyValidators({ value, validators }: ValidatorFn): ValidationResult {
  let validatorResult: ValidationResult = {};
  validators.every(validate => {
    validatorResult = validate(value);
    return !validatorResult.error;
  });
  return validatorResult;
}

export function minLengthValidator(value: string): ValidationResult {
  return value.length < 1 ? { error: ERROR_MAP.REQUIRED_FIELD, color: ValidationColor.error } : VALIDATOR_SUCCESS;
}

export function emailValidator(value: string): ValidationResult {
  return !isValidEmail(value) ? { error: ERROR_MAP.INVALID_EMAIL, color: ValidationColor.error } : VALIDATOR_SUCCESS;
}

export function phoneValidator(value: string): ValidationResult {
  return PHONE_NUMBER_REGEX.test(value)
    ? VALIDATOR_SUCCESS
    : { error: ERROR_MAP.INVALID_PHONE_NUMBER, color: ValidationColor.error };
}

const FIELD_VALIDATORS: FIELD_TYPE = Object.freeze({
  [FIELDS.email]: [minLengthValidator, emailValidator],
  [FIELDS.phone]: [minLengthValidator, phoneValidator],
  [FIELDS.defaultType]: [minLengthValidator],
});

/**
 * This routine validates and returns the final final state
 * @param fieldType
 * @param value
 * @returns the form validated state
 */
export function validateAndGetFormData(fieldType: string, value: string): FormState {
  const validatorType = FIELD_VALIDATORS[fieldType] ? fieldType : 'defaultType';
  const validator = applyValidators({ value, validators: FIELD_VALIDATORS[validatorType] });
  return {
    [fieldType]: {
      value,
      color: validator?.color,
      errorMessage: validator?.error,
    },
  };
}

/**
 * A simple utility to check the form validity status
 * @param formData
 * @returns {boolean} if all the form fields are valid
 */
export const isFormValid = (formData: FormState): boolean | number => {
  const formValues = Object.values(formData);
  return formValues.length && !formValues?.some(({ errorMessage }) => errorMessage);
};
